import { BrandAppConfig } from "@/redux/types";
import {
  ShoppingCartItem,
  UserRetailerShoppingCart,
} from "@brandclub/common-ui";
import { formatMoney } from "./string";

const safeSum = (a?: number, b?: number) => {
  if (!a || isNaN(a)) a = 0;
  if (!b || isNaN(b)) b = 0;
  return a + b;
};

type ItemWithQuantity = {
  quantity?: number;
};
export const sumItemQuantities = (
  items: (ItemWithQuantity | undefined)[] | undefined
) => {
  if (!items) {
    return 0;
  }
  return items?.reduce(
    (total, item) => safeSum(total, item ? item.quantity ?? 1 : 0),
    0
  );
};

type ItemWithItems = {
  items?: ItemWithQuantity[];
};
export const sumAllItemQuantities = (item: (ItemWithItems | undefined)[]) => {
  return (
    item?.reduce(
      (total, i) => safeSum(total, sumItemQuantities(i?.items)),
      0
    ) || 0
  );
};

export const flattenCartItems = (carts: Partial<UserRetailerShoppingCart>[]) =>
  carts
    .flatMap((c) => c.items)
    .filter((i): i is ShoppingCartItem => Boolean(i));

export const createRetailerSkuMap = (
  carts: Partial<UserRetailerShoppingCart>[]
) => new Map(flattenCartItems(carts).map((item) => [item.retailerSku, item]));

export const getEntityImage = (
  id: number | string,
  type: "brand" | "product" | "retailer"
) => {
  if (type === "retailer") {
    return `https://media.brandclub.com/retailer_logo/${id}.webp`;
  }

  return `https://s3-us-west-2.amazonaws.com/stackline-product-images/${id}.jpg`;
};

const getTotal = (products: any[]) => {
  return products.reduce((acc, item) => {
    return acc + item.retailPrice * item.quantity;
  }, 0);
};

export const calculateTotalOfAllCarts = (carts: any[]) => {
  const total = carts.reduce((acc, cart) => {
    return acc + getTotal(cart.items ?? []);
  }, 0);
  return formatMoney(total);
};

export const calculateTotal = (products: any[]) => {
  const total = getTotal(products);

  return formatMoney(total);
};

type RemoveFunctionFields<T> = T extends (...args: any[]) => any
  ? never
  : T extends object
  ? { [K in keyof T]: RemoveFunctionFields<T[K]> }
  : T;

/**
 * Removes function fields from an object recursively.
 *
 * @param input - The input object.
 * @returns - The object with function fields removed.
 */
export const removeFunctionFields = <T extends object>(
  input: T
): RemoveFunctionFields<T> => {
  if (typeof input !== "object" || input === null) {
    return input as RemoveFunctionFields<T>;
  }

  if (Array.isArray(input)) {
    return input
      .filter((item) => typeof item !== "function")
      .map((item) => removeFunctionFields(item)) as RemoveFunctionFields<T>;
  }

  const result: any = {};

  for (const [key, value] of Object.entries(input)) {
    if (typeof value === "function") {
      continue;
    } else if (typeof value === "object" && value !== null) {
      result[key] = removeFunctionFields(value);
    } else {
      result[key] = value;
    }
  }

  return result as RemoveFunctionFields<T>;
};

export const getMobileAppIds = (response?: BrandAppConfig) => {
  if (!response) {
    return {
      appleAppId: "",
      appleUniversalAppId: "",
      androidAppId: "",
    };
  }

  const androidLink = response?.playStoreUrl;
  const iosLink = response?.appStoreUrl;

  let androidAppId = "";
  let appleAppId = "";

  // Extract Android app ID using URL API (it's the "id" query parameter)
  // e.g. https://play.google.com/store/apps/details?id=com.brandclub.petarmor
  if (androidLink) {
    try {
      const url = new URL(androidLink);
      androidAppId = url.searchParams.get("id") || "";
    } catch (error) {
      console.error("Error parsing Android URL:", error);
    }
  }

  // Extract iOS app ID using a regex (pattern: /id followed by numbers/)
  // e.g. https://apps.apple.com/us/app/petarmor/id6737353545
  if (iosLink) {
    const iosMatch = iosLink.match(/\/id(\d+)/);
    if (iosMatch && iosMatch[1]) {
      appleAppId = iosMatch[1];
    }
  }

  const mobileApp = response?.apps?.find((app) => {
    return app?.appId.startsWith("com.");
  });

  const appleUniversalAppId = mobileApp?.appId;

  return {
    appleAppId,
    appleUniversalAppId,
    androidAppId,
  };
};

export const getMobileOperatingSystem = () => {
  var userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;

  if (/android/i.test(userAgent)) {
    return "Android";
  }

  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return "iOS";
  }

  return "unknown";
};
